import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { getMetricDescription } from './metricDescriptions';

interface MetricInfoTooltipProps {
  metricKey: string;
}

/**
 * Ícone de informação com popover explicando o racional de cálculo do
 * indicador (fonte: metricDescriptions.ts). Abre por clique para funcionar
 * também em telas touch; fecha ao clicar fora.
 */
export const MetricInfoTooltip: React.FC<MetricInfoTooltipProps> = ({ metricKey }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const description = getMetricDescription(metricKey);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!description) return null;

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="text-slate-400 hover:text-emerald-600 transition-colors"
        aria-label={`Como é calculado: ${description.label}`}
      >
        <Info size={13} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 p-4 z-50 text-left normal-case font-normal cursor-default">
          <p className="text-sm font-semibold text-slate-900">{description.label}</p>
          <p className="text-xs text-slate-600 mt-1">{description.short}</p>
          <div className="mt-2 px-2 py-1.5 bg-slate-50 rounded border border-slate-100">
            <p className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap">{description.formula}</p>
          </div>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">{description.rationale}</p>
        </div>
      )}
    </span>
  );
};

export default MetricInfoTooltip;
